import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { BrandBadge } from '@/components/ui/BrandBadge';

// Imatge del panell esquerre — substitueix per la URL o import de la teva foto:
// import loginPhoto from '../assets/login-photo.jpg';
const LOGIN_PHOTO_URL = '/taller_%20equiro.webp';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuth();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!identifier || !password) {
      setError('Si us plau, introdueix usuari i contrasenya');
      return;
    }
    try {
      await login(identifier, password);
      navigate(from, { replace: true });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error en iniciar sessio';
      setError(errorMessage);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Panell esquerre: foto ─────────────────────────────────────────────── */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        {/* Gradient sempre visible de fons */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(150deg, #0f172a 0%, #1e3a8a 45%, #1d4ed8 100%)' }}
        />
        {/* Foto per sobre del gradient (si no carrega, el gradient queda visible) */}
        {LOGIN_PHOTO_URL && (
          <img
            src={LOGIN_PHOTO_URL}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {/* Overlay fosc per llegibilitat del text */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Contingut sobre la foto */}
        <div className="relative z-10 h-full flex flex-col justify-between p-10">
          <div className="flex flex-wrap gap-2">
            <BrandBadge name="EQUIRO EQUIPAMENT" color="#1A3F82" />
            <BrandBadge name="SPOKO RAMPS" color="#FF2300" />
            <BrandBadge name="MANUFACTURES DEL TUB" color="#761823" />
            <BrandBadge name="CONART" color="#181B21" />
          </div>

          <div>
            <p className="text-blue-300 text-sm font-medium mb-2 tracking-wide uppercase">
              Roig i Fills Associats S.L.
            </p>
            <h2 className="text-white text-4xl font-bold leading-tight">
              Sistema de Gestió<br />Empresarial Unificat
            </h2>
          </div>
        </div>
      </div>

      {/* ── Panell dret: fons blau + formulari ───────────────────────────────── */}
      <div
        className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
        style={{ background: 'linear-gradient(150deg, #0f172a 0%, #1e3a8a 45%, #1d4ed8 100%)' }}
      >
        <div className="w-full max-w-sm">

          {/* Targeta formulari */}
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-10 space-y-6">

            {/* Logo + títol */}
            <div className="text-center space-y-3">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center font-bold text-base text-white shadow-md">
                  RF
                </div>
              </div>
              <div>
                <h1 className="text-sm font-semibold text-gray-800">ROIG I FILLS ASSOCIATS S.L.</h1>
                <p className="text-xs text-gray-400 mt-0.5">Sistema de Gestió Empresarial Unificat</p>
              </div>
              {/* Badges visibles només en mòbil (l'esquerre ja els mostra en desktop) */}
              <div className="flex flex-wrap items-center justify-center gap-2 lg:hidden">
                <BrandBadge name="EQUIRO EQUIPAMENT" color="#1A3F82" />
                <BrandBadge name="SPOKO RAMPS" color="#FF2300" />
                <BrandBadge name="MANUFACTURES DEL TUB" color="#761823" />
                <BrandBadge name="CONART" color="#181B21" />
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-xs">{error}</p>
              </div>
            )}

            {/* Formulari */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="identifier" className="text-gray-700">Usuari</Label>
                <Input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="nom.usuari"
                  autoComplete="username"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-gray-700">Contrasenya</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="La teva contrasenya"
                    autoComplete="current-password"
                    className="pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox id="remember" />
                  <span className="text-xs text-muted-foreground">Recorda&apos;m</span>
                </label>
                <Button type="button" variant="link" className="h-auto p-0 text-xs font-medium">
                  Has oblidat la contrasenya?
                </Button>
              </div>

              <Button type="submit" disabled={isLoading} className="w-full gap-2">
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Iniciant sessió...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Iniciar Sessió
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-xs text-gray-400">
              Roig i Fills Associats S.L. · 2026
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
